<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { useIntervalFn } from '@vueuse/core';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { util } from 'greenwebjs';
import { computed, ref } from 'vue';
import type { Offer } from '~/lib/types';

dayjs.extend(relativeTime);

const props = defineProps<{
  offers: Offer[];
}>();

const inspectedOffer = ref<Offer | null>(null);

const intervalCounter = ref(0);
useIntervalFn(() => {
  intervalCounter.value = (intervalCounter.value + 1) % 60;
}, 1000);

const calculatePrice = (offer: Offer) => {
  const xchRequested = offer.requestedPayments.find((p) => !p.nft && !p.cat)?.amount || 0;
  const xchOffered = offer.offeredCoins.find((c) => !c.nft && !c.cat)?.amount || 0;

  const nonXchRequested = offer.requestedPayments.find((p) => p.nft || p.cat)?.amount || 0;
  const nonXchOffered = offer.offeredCoins.find((c) => c.nft || c.cat)?.amount || 0;

  if ((xchRequested > 0 && nonXchOffered > 0) || (xchOffered > 0 && nonXchRequested > 0)) {
    const xchAmount = xchRequested > 0 ? xchRequested : xchOffered;
    const nonXchAmount = nonXchRequested > 0 ? nonXchRequested : nonXchOffered;
    const pricePerUnit = xchAmount / 10 ** 12 / (nonXchAmount / 1000);
    return Math.floor(pricePerUnit * 10000) / 10000;
  }

  return 'N/A';
};

const offersWithPrice = computed(() =>
  props.offers.map((offer) => ({
    ...offer,
    price: calculatePrice(offer),
  }))
);

const hasOffers = computed(() => props.offers.length > 0);
</script>

<template>
  <div>
    <Table class="w-full" v-if="hasOffers">
      <TableHeader>
        <TableRow>
          <TableHead>Requested</TableHead>
          <TableHead>Offered</TableHead>
          <TableHead class="text-end">Price</TableHead>
          <TableHead class="text-end">Time Received</TableHead>
          <TableHead class="text-end">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody class="font-mono">
        <TableRow v-for="(offer, index) in offersWithPrice" :key="index">
          <TableCell>
            <div class="flex flex-col gap-1">
              <template v-for="requestedPayment in offer.requestedPayments">
                <Nft v-if="requestedPayment.nft" :nft="requestedPayment.nft" />
                <Cat v-else-if="requestedPayment.cat" :amount="requestedPayment.amount" :cat="requestedPayment.cat" />
                <div v-else class="inline-flex gap-1 items-center">
                  <img class="h-6 w-6" src="https://icons.dexie.space/xch.webp" />{{
                    util.formatChia(requestedPayment.amount)
                  }}
                  XCH
                </div>
              </template>
            </div>
          </TableCell>
          <TableCell>
            <div class="flex flex-col gap-1">
              <template v-for="offeredCoin in offer.offeredCoins">
                <Nft v-if="offeredCoin.nft" :nft="offeredCoin.nft" />
                <Cat v-else-if="offeredCoin.cat" :amount="offeredCoin.amount" :cat="offeredCoin.cat" />
                <div v-else class="inline-flex gap-1 items-center">
                  <img class="h-6 w-6" src="https://icons.dexie.space/xch.webp" />{{
                    util.formatChia(offeredCoin.amount)
                  }}
                  XCH
                </div>
              </template>
            </div>
          </TableCell>
          <TableCell
            ><div class="flex justify-end items-center gap-1">
              {{ offer.price }}
              {{ offer.price !== 'N/A' ? 'XCH' : '' }}
            </div></TableCell
          >
          <TableCell class="text-end" :data-counter="intervalCounter">{{ dayjs(offer.timestamp).fromNow() }}</TableCell>
          <TableCell class="text-end">
            <Button @click="inspectedOffer = offer">Inspect</Button>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>

    <div v-else class="text-center py-8 text-gray-500 dark:text-gray-400">
      <p>Waiting for offers to be published via Splash...</p>
    </div>

    <Dialog :open="!!inspectedOffer" @update:open="(open) => !open && (inspectedOffer = null)">
      <DialogContent class="max-w-3xl dark:text-white">
        <DialogHeader>
          <DialogTitle>Offer Details</DialogTitle>
        </DialogHeader>
        <div class="mt-4">
          <div class="mb-8 gap-4 flex flex-col">
            <div class="flex flex-col space-y-1.5">
              <span class="text-sm font-medium leading-none">Requested</span>
              <div
                class="flex flex-col gap-2 w-full rounded-md border bg-neutral-100 border-neutral-200 dark:bg-neutral-900 dark:border-neutral-700 px-3 py-2 text-sm"
              >
                <template v-for="requestedPayment in inspectedOffer?.requestedPayments">
                  <Nft v-if="requestedPayment.nft" :nft="requestedPayment.nft" />
                  <Cat v-else-if="requestedPayment.cat" :amount="requestedPayment.amount" :cat="requestedPayment.cat" />
                  <div v-else class="inline-flex gap-1 items-center">
                    <img class="h-6 w-6" src="https://icons.dexie.space/xch.webp" />{{
                      util.formatChia(requestedPayment.amount)
                    }}
                    XCH
                  </div>
                </template>
              </div>
            </div>
            <div class="flex flex-col space-y-1.5">
              <span class="text-sm font-medium leading-none">Offered</span>
              <div
                class="flex flex-col gap-2 w-full rounded-md border bg-neutral-100 border-neutral-200 dark:bg-neutral-900 dark:border-neutral-700 px-3 py-2 text-sm"
              >
                <template v-for="offeredCoin in inspectedOffer?.offeredCoins">
                  <Nft v-if="offeredCoin.nft" :nft="offeredCoin.nft" />
                  <Cat v-else-if="offeredCoin.cat" :amount="offeredCoin.amount" :cat="offeredCoin.cat" />
                  <div v-else class="inline-flex gap-1 items-center">
                    <img class="h-6 w-6" src="https://icons.dexie.space/xch.webp" />{{
                      util.formatChia(offeredCoin.amount)
                    }}
                    XCH
                  </div>
                </template>
              </div>
            </div>
          </div>
          <span class="text-sm font-medium leading-none">Raw Offer File</span>
          <pre
            class="border border-neutral-200 dark:border-neutral-700 bg-neutral-100 dark:bg-neutral-900 p-2 rounded overflow-x-auto whitespace-pre-wrap break-all text-xs"
            >{{ inspectedOffer?.offer }}
          </pre>
        </div>
      </DialogContent>
    </Dialog>
  </div>
</template>
